import { isFunction, isString } from 'lodash-es'

import { ErrorCode } from './ErrorCode'
import { iterateError } from './utils'

export type AppErrorOptions = { code: ErrorCode; message?: string } & ErrorOptions

export class AppError extends Error {
	static caused(error?: unknown, code?: ErrorCode | ErrorCode[]): boolean {
		for (const current of iterateError(error)) {
			if (this.is(current, code)) {
				return true
			}
		}
		return false
	}

	/**
	 * Ensures that the error you throw or pass further is an AppError
	 * @example
	 * try {
	 *   // ...
	 * } catch (error) {
	 *   throw AppError.ensure(
	 *     error,
	 *     (e) =>
	 *       new AppError({
	 *         code: ErrorCode.fare_unavailable,
	 *         message: 'Pricing call failed',
	 *         cause: e,
	 *       }),
	 *   )
	 * }
	 */
	static ensure(error: unknown, factory: (error: unknown) => AppError) {
		return this.is(error) ? error : factory(error)
	}

	static from(code: ErrorCode, message?: string) {
		return new this({ code, message })
	}

	static is(error?: unknown, code?: ErrorCode | ErrorCode[]): error is AppError {
		return isSpecificError(AppError, error, code)
	}

	static match<R>(
		error: unknown,
		errorMap: Record<'_', (e: unknown) => R> & Partial<Record<ErrorCode, (e: AppError) => R>>,
	): R {
		if (this.is(error)) {
			const valueMapper = errorMap[error.code]
			if (isFunction(valueMapper)) {
				return valueMapper(error)
			}
		}
		return errorMap._(error)
	}

	static matchAppError<R = never>(errorMap: Partial<Record<ErrorCode | '_', ((e: AppError) => R) | ErrorCode>>) {
		return function apiErrorMapper(error: unknown): R {
			if (!AppError.is(error)) {
				throw error
			}
			const valueMapper = errorMap[error.code] ?? errorMap['_']
			if (isFunction(valueMapper)) {
				return valueMapper(error)
			}
			throw new AppError({
				code: isString(valueMapper) ? valueMapper : ErrorCode.unknown,
				message: error.message,
				cause: error,
			})
		}
	}

	code: ErrorCode

	constructor(options?: AppErrorOptions) {
		super(options?.message ?? options?.code ?? ErrorCode.unknown, options)
		this.code = options?.code ?? ErrorCode.unknown
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isSpecificError<T extends new (...args: any[]) => AppError>(
	errorClass: T,
	error: unknown,
	code?: ErrorCode | ErrorCode[],
): error is T {
	return (
		error instanceof errorClass && (!code || (Array.isArray(code) ? code.includes(error.code) : error.code === code))
	)
}
